import productColors from "@/mockDatas/productColors.json";

function removeVietnameseTones(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .toLowerCase()
    .trim();
}

/**
 * Làm sạch chuỗi để so sánh từ khóa chính của nhóm hàng:
 * Bỏ các tiền tố ngành hàng như "cafe", "ca phe", "tra", "tea", "nong san",...
 */
function cleanKey(str: string): string {
  return removeVietnameseTones(str)
    .replace(/o\s*long/gi, "oolong")
    .replace(/\bcafe\b/gi, "")
    .replace(/\bca phe\b/gi, "")
    .replace(/\btra\b/gi, "")
    .replace(/\btea\b/gi, "")
    .replace(/\bnong san\b/gi, "")
    .replace(/\bthuc pham\b/gi, "")
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Bảng màu dự phòng riêng biệt cho các nhóm con của từng ngành hàng
 * (đảm bảo mỗi nhóm con luôn có màu riêng, KHÔNG trùng màu của mã ngành hàng)
 */
const SECTOR_SUB_PALETTES: Record<string, string[]> = {
  CAFE: [
    "#4E342E", // Nâu đậm rang xay
    "#795548", // Nâu sữa hòa tan
    "#3E2723", // Nâu espresso đậm
    "#6D4C41", // Nâu mocha
    "#8D6E63", // Nâu hạt cafe
    "#556B2F", // Xanh ô liu (nhân xanh)
    "#A1887F", // Nâu sáng
    "#37474F", // Xám than rang
  ],
  TRA: [
    "#2E7D32", // Xanh sâm dứa
    "#00897B", // Xanh ngọc hòa tan
    "#388E3C", // Xanh trà lài
    "#0D5735", // Xanh oolong đậm
    "#43A047", // Xanh tươi túi lọc
    "#8D2616", // Đỏ hồng trà / trà đen
    "#1B5E20", // Xanh trà bắc
    "#558B2F", // Xanh mạ
  ],
  NS: [
    "#C67D0A", // Vàng gạo
    "#689F38", // Xanh đậu
    "#D32F2F", // Đỏ ớt
    "#37474F", // Đen tiêu
    "#AF4F13", // Đất nung
    "#D97706", // Cam hạt
    "#8D6E63", // Nâu hạt dẻ
  ],
  TPCB: [
    "#E65100", // Cam đậm phở
    "#D97706", // Vàng bánh tráng
    "#786043", // Nâu miến
    "#E64A19", // Đỏ bún
    "#EA6222", // Cam cháy
    "#C2410C", // Đỏ cam
  ],
};

export interface TabIdentifier {
  id?: string;
  label?: string;
  ma_nganh_hang?: string;
}

/**
 * Lấy mã màu chuẩn cho tab/pill theo cấu hình productConfig (hoặc fallback về productColors)
 * - Trang mẹ: Hiển thị màu của mã ngành hàng (TRA, CAFE, NS, TPCB)
 * - Trang con: Màu của mã ngành hàng CHỈ áp dụng cho nút "Tất cả".
 *   Các nút nhóm hàng con BẮT BUỘC áp dụng màu riêng của nhóm hàng.
 */
export function getTabColor(
  identifier: TabIdentifier | string,
  customConfig?: any,
  currentSectorCode?: string
): string {
  const activeConfig = customConfig || productColors;
  const categories = (activeConfig?.categories || {}) as Record<
    string,
    {
      ma_nganh_hang?: string;
      name: string;
      defaultColor: string;
      items?: Record<string, string>;
    }
  >;

  const id = typeof identifier === "string" ? identifier : identifier?.id || "";
  const label =
    typeof identifier === "string" ? identifier : identifier?.label || "";
  const rawSector =
    (typeof identifier === "object" ? identifier?.ma_nganh_hang : undefined) ||
    currentSectorCode ||
    "";
  const sectorCode = rawSector.toUpperCase().trim();

  // 1. Nếu là tab "all" (Tất cả)
  if (
    id === "all" ||
    label.toLowerCase() === "tất cả" ||
    label.toLowerCase() === "tat ca"
  ) {
    // Nếu ở trang con (có sectorCode, ví dụ "CAFE") -> màu của mã ngành hàng Cà phê!
    if (sectorCode) {
      for (const cat of Object.values(categories)) {
        if (
          cat.ma_nganh_hang &&
          cat.ma_nganh_hang.toUpperCase() === sectorCode
        ) {
          return cat.defaultColor || "#0b5d3b";
        }
      }
    }
    // Ở trang mẹ -> màu thương hiệu xanh Việt Thiên
    return "#0b5d3b";
  }

  // 2. NẾU Ở TRANG MẸ (currentSectorCode không được truyền):
  // Các pill là ngành hàng (TRA, CAFE, NS, TPCB) -> Áp dụng màu của mã ngành hàng
  if (!currentSectorCode) {
    const idUpper = id.toUpperCase();
    for (const cat of Object.values(categories)) {
      if (
        cat.ma_nganh_hang &&
        (cat.ma_nganh_hang.toUpperCase() === idUpper ||
          cat.ma_nganh_hang.toUpperCase() === label.toUpperCase())
      ) {
        return cat.defaultColor;
      }
    }

    const normalizedId = removeVietnameseTones(id);
    const normalizedLabel = removeVietnameseTones(label);

    for (const [catKey, cat] of Object.entries(categories)) {
      if (
        catKey === id.toLowerCase() ||
        catKey === normalizedId ||
        catKey.replace(/_/g, " ") === normalizedLabel ||
        removeVietnameseTones(cat.name || "") === normalizedLabel
      ) {
        return cat.defaultColor;
      }
    }
  }

  // 3. NẾU Ở TRANG CON (currentSectorCode tồn tại, ví dụ "CAFE"):
  // TUYỆT ĐỐI KHÔNG DÙNG defaultColor của mã ngành hàng ở đây!
  // Tìm màu của nhóm hàng trong cat.items (dùng cleanKey và normalizedLabel linh hoạt)
  const cleanedLabel = cleanKey(label);
  const normalizedLabel = removeVietnameseTones(label);

  if (sectorCode) {
    for (const cat of Object.values(categories)) {
      if (
        cat.ma_nganh_hang &&
        cat.ma_nganh_hang.toUpperCase() === sectorCode &&
        cat.items
      ) {
        // a. So khớp cleanKey chính xác
        for (const [subKey, color] of Object.entries(cat.items)) {
          const cleanedSubKey = cleanKey(subKey);
          if (
            cleanedLabel === cleanedSubKey ||
            (cleanedSubKey && cleanedLabel.includes(cleanedSubKey)) ||
            (cleanedLabel && cleanedSubKey.includes(cleanedLabel))
          ) {
            return color;
          }
        }

        // b. So khớp normalizedLabel
        for (const [subKey, color] of Object.entries(cat.items)) {
          const normSubKey = removeVietnameseTones(subKey);
          if (
            label.toLowerCase().includes(subKey.toLowerCase()) ||
            normalizedLabel.includes(normSubKey) ||
            normSubKey.includes(normalizedLabel)
          ) {
            return color;
          }
        }
      }
    }
  }

  // 4. Quét toàn bộ sub-items nếu chưa tìm thấy trong sector hiện tại
  for (const cat of Object.values(categories)) {
    if (cat.items) {
      for (const [subKey, color] of Object.entries(cat.items)) {
        const cleanedSubKey = cleanKey(subKey);
        if (
          cleanedLabel === cleanedSubKey ||
          (cleanedSubKey && cleanedLabel.includes(cleanedSubKey)) ||
          (cleanedLabel && cleanedSubKey.includes(cleanedLabel))
        ) {
          return color;
        }
      }
    }
  }

  // 5. Nếu nhóm hàng chưa có trong items của JSON:
  // Chọn màu riêng biệt từ bảng màu nhóm con của ngành hàng (KHÔNG dùng màu mã ngành hàng!)
  const subPalette =
    SECTOR_SUB_PALETTES[sectorCode] ||
    activeConfig?.defaultPalette ||
    productColors.defaultPalette;

  let hashVal = 0;
  const hashStr = id || label;
  for (let i = 0; i < hashStr.length; i++) {
    hashVal = (hashVal << 5) - hashVal + hashStr.charCodeAt(i);
    hashVal |= 0;
  }
  const colorIdx = Math.abs(hashVal) % subPalette.length;
  return subPalette[colorIdx];
}

export function getProductHoverColor(
  product?: any,
  customConfig?: any
): string {
  if (!product) return "#008748";

  // 1. Direct color field from backend API if available
  const directColor =
    product.color ||
    product.ma_mau ||
    product.hover_color ||
    product.background_color;
  if (directColor && typeof directColor === "string") {
    return directColor;
  }

  // Combine product name and category fields for text matching
  const name = (product.ten_vt || product.title || "").toLowerCase();
  const category = (
    product.ten_nvt ||
    product.ten_dvt ||
    product.nh_vt_1 ||
    ""
  ).toLowerCase();
  const fullText = `${name} ${category}`;
  const normalizedText = removeVietnameseTones(fullText);

  const activeConfig = customConfig || productColors;
  const categories = (activeConfig?.categories || {}) as Record<
    string,
    {
      ma_nganh_hang?: string;
      name: string;
      defaultColor: string;
      items: Record<string, string>;
    }
  >;

  // 2. Check if product has explicit ma_nganh_hang
  if (product?.ma_nganh_hang) {
    for (const cat of Object.values(categories)) {
      if (cat.ma_nganh_hang === product.ma_nganh_hang) {
        for (const [subKey, color] of Object.entries(cat.items || {})) {
          const normalizedSubKey = removeVietnameseTones(subKey);
          if (
            fullText.includes(subKey.toLowerCase()) ||
            normalizedText.includes(normalizedSubKey)
          ) {
            return color;
          }
        }
        return cat.defaultColor;
      }
    }
  }

  // 3. Check specific subcategory items (most specific keyword match)
  for (const catKey of Object.keys(categories)) {
    const cat = categories[catKey];
    for (const [subKey, color] of Object.entries(cat.items || {})) {
      const normalizedSubKey = removeVietnameseTones(subKey);
      if (
        fullText.includes(subKey.toLowerCase()) ||
        normalizedText.includes(normalizedSubKey)
      ) {
        return color;
      }
    }
  }

  // 4. Check general category match
  for (const catKey of Object.keys(categories)) {
    const cat = categories[catKey];
    const catName = (cat.name || "").toLowerCase();
    const normalizedCatName = removeVietnameseTones(cat.name || "");
    if (
      (catName && fullText.includes(catName)) ||
      (normalizedCatName && normalizedText.includes(normalizedCatName)) ||
      fullText.includes(catKey.replace(/_/g, " "))
    ) {
      return cat.defaultColor;
    }
  }

  // 5. Deterministic hash palette fallback
  const key = product.ma_vt || product._id || product.ten_vt || "";
  if (key) {
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = (hash << 5) - hash + key.charCodeAt(i);
      hash |= 0;
    }
    const palette =
      activeConfig?.defaultPalette || productColors.defaultPalette;
    const idx = Math.abs(hash) % palette.length;
    return palette[idx];
  }

  return "#008748";
}
